import { LocalStorageOptions } from "@pathwright/utils/src/storage/useLocalStorage"
import { FormikValues } from "formik"
import debounce from "lodash/debounce"
import { useCallback, useEffect, useMemo, useState } from "react"
import isEqual from "react-fast-compare"
import { usePrevious } from "react-use"
import useFormStateStorage, {
  FormStateKey,
  FormStateStorage
} from "./useFormStateStorage"

type FormState = {
  values: FormikValues
  initialValues: FormikValues
}

// Manually count how many fields differ from their initial value.
const getNumberOfChangedFields = (formState: FormState) => {
  const keys = Object.keys(formState.values)
  const numberOfChanges = keys.reduce((numberOfChanges, key) => {
    // Compare initialValues against current values, per key.
    const increment = isEqual(
      formState.initialValues[key],
      formState.values[key]
    )
      ? 0
      : 1
    return numberOfChanges + increment
  }, 0)

  return numberOfChanges
}

export type DiscardFormState<T = any> = (
  preserve?: boolean,
  // Optional values to use when preserving. Lets callers snapshot submitted
  // values at submit start so a mid-submit form reset cannot clobber them.
  valuesOverride?: T
) => void

export type SetFormState<T = any> = (
  formState: Exclude<FormStateStorage<T>, null | undefined>
) => void

export type DiscardFormStateReturn = {
  discardCount: number
  discardFormState: DiscardFormState
}

export type DiscardFormStateOptions<T = any> = {
  key?: FormStateKey
  formStateMigrationKey?: string
  formState: Exclude<FormStateStorage<T>, null | undefined>
  setFormState: SetFormState<T>
  localStorageOptions?: LocalStorageOptions
}

// Handles setting initial form state based on stored form state and
// synchronizing provided form state with stored form state.
const useDiscardFormState = <T = any>({
  key,
  formStateMigrationKey,
  formState,
  setFormState,
  localStorageOptions
}: DiscardFormStateOptions<T>) => {
  const [formStateStorage, setFormStorageState, formStateStorageMeta] =
    useFormStateStorage(formState.initialValues, {
      key,
      formStateMigrationKey,
      localStorageOptions
    })
  const [numberOfChanges, setNumberOfChangedFields] = useState<number>(0)

  const handleFormChange = useCallback((formState: any) => {
    // The form state has been modified.
    setFormStorageState(formState.values)
    setNumberOfChangedFields(getNumberOfChangedFields(formState))
  }, [])

  const debouncedHandleFormChange = useMemo(
    () =>
      debounce(
        handleFormChange,
        // NOTE: for now choosing a very low delay to avoid an edge case with fields
        // that 1) share same storage key and 2) are unmounted and mounted in quick succession.
        // This can lead to subsequently mounted fields having stale stored value.
        50,
        {
          leading: false,
          trailing: true
        }
      ),
    []
  )

  const debouncedHandleGlobalStorageChange = useMemo(
    () =>
      debounce(
        (formStateStorage, formState) => {
          // An undefined value indicates the storage key has been completely
          // removed, while an otherwise nullish value indicates the storage
          // data has been discarded. Both cases indicate that the "local"
          // storage should be discarded.
          if (!formStateStorage) {
            // If formStateStorage is undefined, we can safely preserve the
            // formState. Otherwise we discard formState changes.
            const preserve = formStateStorage === undefined
            discardFormState(preserve)
          } else {
            // Simply update the current values.
            setFormState({
              values: formStateStorage.values,
              initialValues: formState.initialValues
            })
          }
        },
        300,
        {
          leading: true,
          trailing: true
        }
      ),
    []
  )

  // Must flush any debounced changes before unmounting.
  useEffect(() => {
    return () => {
      debouncedHandleFormChange.flush()
      debouncedHandleGlobalStorageChange.flush()
    }
  }, [])

  const previousFormState = usePrevious(formState)
  // Only respond to changes to formState after initial setup.
  useEffect(() => {
    // The form state has been modified.
    if (
      previousFormState &&
      (previousFormState.initialValues !== formState.initialValues ||
        previousFormState.values !== formState.values)
    ) {
      // Avoid debouncing if form state is being reset to initial values.
      // This could be due to user action, and the form could be unmounting,
      // and we need this change to be immediate, so as to update local storage state
      // before useFormStateStorage unmounts.

      if (
        formState.values === formState.initialValues ||
        // Also avoid debouncing in test environment to ensure predictable test results.
        process.env.NODE_ENV === "test"
      ) {
        handleFormChange(formState)
      } else {
        debouncedHandleFormChange(formState)
      }
    }
  }, [formState.initialValues, formState.values])

  const previousFormStateStorage = usePrevious(formState)
  // When formStateStorage changes and the change source was
  // "global", meaning this change occurred in another web page,
  // update the current formState, which in turn will update the
  // "local" storage.
  useEffect(() => {
    if (
      previousFormStateStorage &&
      previousFormStateStorage !== formStateStorage &&
      formStateStorageMeta.source === "global"
    ) {
      debouncedHandleGlobalStorageChange(formStateStorage, formState)
    }
  }, [formStateStorage])

  useEffect(() => {
    // Get the latest formStateStorage from the ref, to avoid closure issues.
    // Since this is a one-time setup effect, we don't need to add formStateStorage
    // to the dependency array, or use exactly what's in formStateStorage variable.
    const currentFormStateStorage = formStateStorageMeta.valueRef.current
    if (
      currentFormStateStorage &&
      !isEqual(formState.values, currentFormStateStorage.values)
    ) {
      // The form state has not been modified but differs from the
      // local form state, presumably upon initial form mount.
      setFormState({
        values: currentFormStateStorage.values,
        initialValues: formState.initialValues
      })
    }
  }, [])

  // Reset form to initial state, or preserve submitted values as the new baseline.
  const discardFormState: DiscardFormState<T> = (
    preserve = false,
    valuesOverride
  ) => {
    // Drop any in-flight draft write so a trailing debounce cannot revive
    // values we are about to discard.
    debouncedHandleFormChange.cancel()

    // Discarding form state might require preserving the form's
    // current (or explicitly provided) state, for instance, after successful
    // form submission.
    if (preserve === true) {
      const preservedValues = valuesOverride ?? formState.values
      setFormState({
        values: preservedValues,
        initialValues: preservedValues
      })
    } else {
      // Here we're discarding form's current state, which will result in
      // also discarding stored form state.
      setFormState({
        values: formState.initialValues,
        initialValues: formState.initialValues
      })
    }

    // HACK ALERT...

    // Sort of rerverse the logic for formStateStorage. The main reason for
    // handling discard logic differently for formStateStorage based on preserve
    // flag is for "global" syncing of localStorage state. The type of discard
    // communicates to other web pages about how to set the preserve flag value
    // when discarding changes.
    if (preserve === true) {
      // Completely discard stored form state.
      setFormStorageState()
    } else {
      // Preseve form state key, but clear the value.
      // Only necessary for "global" localStorage syncing.
      setFormStorageState(null)
    }
  }

  return {
    discardCount: numberOfChanges,
    discardFormState
  }
}

export default useDiscardFormState
